import { Alert, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams } from "react-router";

import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaPageComponent from "~/modules/app/components/GaiaPageComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import { RESEARCH_TYPE_PATH } from "~/modules/settings/constants";
import FormTemplateEditor from "~/modules/settings/shared/formTemplateEditor/components/FormTemplateEditor";
import { useSettingsFormTemplateEditorData } from "~/modules/settings/shared/formTemplateEditor/hooks/useSettingsFormTemplateEditorData";

import ObservationTypeFunctionTypeListCard from "./components/ObservationTypeFunctionTypeListCard";
import ObservationTypeUpdateForm, { ObservationTypeUpdateFormInputs } from "./components/ObservationTypeUpdateForm";
import { useSettingsObservationTypeData } from "./hooks/useSettingsObservationTypeData";
import { SettingsObservationTypeEditRoute_GetByIdDocument } from "./queries.generated";

export default function SettingsObservationTypeEditRoute() {
    const { t } = useTranslation("observationType");

    const navigate = useNavigate();

    const { observationTypeId, researchTypeId } = useParams();
    if (!researchTypeId || !observationTypeId) throw new Error("invalid params");

    const { queryData, updateMutation, deleteMutation, addFunctionTypeMutation, removeFunctionTypeMutation } =
        useSettingsObservationTypeData(observationTypeId);

    const {
        addFormTemplateQuestionMutation,
        updateFormTemplateQuestionMutation,
        deleteFormTemplateQuestionMutation,
        handleAddQuestion,
        handleUpdateQuestion,
        editQuestionError,
    } = useSettingsFormTemplateEditorData({
        refetchQueries: [SettingsObservationTypeEditRoute_GetByIdDocument],
        formTemplateId: queryData.observationType?.formTemplate.id,
    });

    const handleSubmit = async (data: ObservationTypeUpdateFormInputs) => {
        await updateMutation.updateObservationType(observationTypeId, data);
        navigate(
            generatePath(RESEARCH_TYPE_PATH, {
                researchTypeId,
            }),
        );
    };

    const handleDelete = async () => {
        await deleteMutation.deleteObservationType(observationTypeId);
        navigate(
            generatePath(RESEARCH_TYPE_PATH, {
                researchTypeId,
            }),
        );
    };

    if (queryData.loading) {
        return <CircularProgress />;
    }

    return (
        <GaiaPageLayoutColumn>
            {queryData.observationType && (
                <>
                    <GaiaPageComponent title={t("settingsObservationTypeEditRoute.title")}>
                        {queryData.error && <Alert severity="error">{queryData.error.message}</Alert>}
                        <ObservationTypeUpdateForm
                            initialData={queryData.observationType}
                            onSubmit={handleSubmit}
                            error={updateMutation.error}
                        />
                    </GaiaPageComponent>
                    <GaiaPageComponent title={t("settingsObservationTypeEditRoute.formTemplateEditorTitle")}>
                        <FormTemplateEditor
                            formTemplateQuestions={queryData.observationType.formTemplate.questions}
                            onAddQuestion={handleAddQuestion}
                            onUpdateQuestion={handleUpdateQuestion}
                            onDeleteQuestion={async id =>
                                await deleteFormTemplateQuestionMutation.deleteFormTemplateQuestion(id)
                            }
                            isAddQuestionLoading={addFormTemplateQuestionMutation.loading}
                            isUpdateQuestionLoading={updateFormTemplateQuestionMutation.loading}
                            isDeleteQuestionLoading={deleteFormTemplateQuestionMutation.loading}
                            addQuestionError={addFormTemplateQuestionMutation.error}
                            editQuestionError={editQuestionError}
                        />
                    </GaiaPageComponent>
                    <GaiaPageComponent
                        title={t("settingsObservationTypeEditRoute.observationTypeFunctionTypeListTitle")}
                        titleVariant="h3"
                    >
                        <ObservationTypeFunctionTypeListCard
                            observationTypeFunctionTypes={queryData.observationType.ecologicalFunctionTypes}
                            researchTypeId={researchTypeId}
                            observationTypeId={observationTypeId}
                            onAddFunctionType={data =>
                                addFunctionTypeMutation.addObservationTypeFunctionType({
                                    observationTypeId,
                                    ecologicalFunctionTypeId: data.ecologicalFunctionTypeId,
                                })
                            }
                            isAddFunctionTypeLoading={addFunctionTypeMutation.loading}
                            addFunctionTypeError={addFunctionTypeMutation.error}
                            onRemoveFunctionType={ecologicalFunctionTypeId =>
                                removeFunctionTypeMutation.removeObservationTypeFunctionType({
                                    observationTypeId,
                                    ecologicalFunctionTypeId,
                                })
                            }
                            isRemoveFunctionTypeLoading={removeFunctionTypeMutation.loading}
                            removeFunctionTypeError={removeFunctionTypeMutation.error}
                        />
                    </GaiaPageComponent>
                    {deleteMutation.error && <Alert severity="error">{deleteMutation.error.message}</Alert>}
                    <GaiaPageComponent title={t("settingsObservationTypeEditRoute.dangerZone")} titleVariant="h3">
                        <GaiaDeleteButton
                            label={t("settingsObservationTypeEditRoute.observationTypeDeleteBtn")}
                            onDelete={handleDelete}
                            isLoading={deleteMutation.loading}
                        />
                    </GaiaPageComponent>
                </>
            )}
        </GaiaPageLayoutColumn>
    );
}
